import { useRef, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { ChevronsLeftRight, Camera } from "lucide-react";
import { Reveal } from "@/components/Reveal";

const WORK_PHOTOS = [
  { src: "/assets/work-1.webp", alt: "Crystal clear basement window after a Sky-High clean", label: "After — crystal clear" },
  { src: "/assets/clean-2.webp", alt: "Spotless window well glass after cleaning", label: "After — inside & out" },
  { src: "/assets/midjob.webp", alt: "Hard-water stained glass mid-job in Billings", label: "Mid-job — hard-water haze" },
];

export const BeforeAfter = () => {
  const [pos, setPos] = useState(55);
  const frameRef = useRef(null);
  const dragging = useRef(false);

  const update = useCallback((clientX) => {
    const rect = frameRef.current?.getBoundingClientRect();
    if (!rect) return;
    const pct = ((clientX - rect.left) / rect.width) * 100;
    setPos(Math.min(97, Math.max(3, pct)));
  }, []);

  const onPointerDown = (e) => {
    dragging.current = true;
    e.currentTarget.setPointerCapture(e.pointerId);
    update(e.clientX);
  };
  const onPointerMove = (e) => {
    if (dragging.current) update(e.clientX);
  };
  const stop = () => {
    dragging.current = false;
  };

  const onKeyDown = (e) => {
    if (e.key === "ArrowLeft") setPos((p) => Math.max(3, p - 4));
    if (e.key === "ArrowRight") setPos((p) => Math.min(97, p + 4));
  };

  return (
    <section
      id="proof"
      data-testid="before-after-section"
      className="border-t border-zinc-200 bg-[#FAFAFA]"
    >
      <div className="mx-auto max-w-[1400px] px-6 py-32 md:px-12 md:py-44">
        <div className="mb-16 flex flex-col gap-6 md:mb-24 md:flex-row md:items-end md:justify-between">
          <Reveal>
            <p className="mb-4 text-xs font-bold uppercase tracking-[0.35em] text-sky-500">
              The Proof
            </p>
            <h2 className="max-w-xl font-display text-4xl font-black leading-[0.95] tracking-tighter text-zinc-950 sm:text-5xl lg:text-6xl">
              Drag to see the difference
            </h2>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="max-w-sm text-sm leading-relaxed text-zinc-500 md:text-base">
              A real Sky-High job in Billings — hard-water haze on the left,
              invisible glass on the right. No filters, just squeegees.
            </p>
          </Reveal>
        </div>

        <Reveal delay={0.1}>
          <div
            ref={frameRef}
            data-testid="before-after-slider"
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={stop}
            onPointerCancel={stop}
            className="image-spotlight relative aspect-[4/3] w-full cursor-ew-resize select-none overflow-hidden rounded-md border border-zinc-200 md:aspect-[16/8]"
            style={{ touchAction: "pan-y" }}
          >
            <img
              src="/assets/clean-main.webp"
              alt="After: spotless, crystal clear window"
              draggable={false}
              className="absolute inset-0 h-full w-full object-cover"
            />
            <div
              className="absolute inset-0"
              style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}
            >
              <img
                src="/assets/dirty-main.webp"
                alt="Before: hard-water stained, streaky window"
                draggable={false}
                className="absolute inset-0 h-full w-full object-cover"
              />
            </div>

            <span className="absolute left-5 top-5 rounded-full bg-zinc-950/80 px-4 py-1.5 font-display text-[11px] font-bold uppercase tracking-[0.25em] text-white backdrop-blur-sm">
              Before
            </span>
            <span className="absolute right-5 top-5 rounded-full bg-sky-500/90 px-4 py-1.5 font-display text-[11px] font-bold uppercase tracking-[0.25em] text-white backdrop-blur-sm">
              After
            </span>

            <div
              className="absolute inset-y-0 z-10"
              style={{ left: `${pos}%` }}
              aria-hidden="true"
            >
              <div className="absolute inset-y-0 -ml-px w-0.5 bg-white shadow-[0_0_20px_rgba(0,0,0,0.35)]" />
              <div
                role="slider"
                tabIndex={0}
                aria-label="Before and after comparison slider"
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={Math.round(pos)}
                onKeyDown={onKeyDown}
                data-testid="before-after-handle"
                className="absolute top-1/2 flex h-14 w-14 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-white text-zinc-950 shadow-xl ring-4 ring-white/30 transition-transform duration-200 hover:scale-110 focus:outline-none focus:ring-sky-500"
              >
                <ChevronsLeftRight className="h-6 w-6" />
              </div>
            </div>
          </div>
        </Reveal>

        <div className="mt-16 grid grid-cols-1 gap-8 sm:grid-cols-3">
          {WORK_PHOTOS.map((photo, i) => (
            <motion.figure
              key={photo.src}
              data-testid={`work-photo-${i + 1}`}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.9, delay: i * 0.12, ease: [0.16, 1, 0.3, 1] }}
              className="group image-spotlight relative aspect-[4/5] overflow-hidden rounded-md border border-zinc-200"
            >
              <img
                src={photo.src}
                alt={photo.alt}
                loading="lazy"
                className="h-full w-full object-cover transition-transform duration-[1.4s] ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-110"
              />
              <span className="absolute bottom-4 left-4 flex items-center gap-2 rounded-full bg-zinc-950/70 px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.2em] text-white backdrop-blur-sm">
                <Camera className="h-3 w-3" />
                {photo.label}
              </span>
            </motion.figure>
          ))}
        </div>
      </div>
    </section>
  );
};
